import React from "react";
import { Link } from "react-router-dom";
import { Lock, Loader2, Inbox, Star } from "lucide-react";
import PublicNav from "@/components/layout/PublicNav";
import Footer from "@/components/layout/Footer";
import { TIERS } from "@/lib/constants";

export function PublicShell({ children }) {
  return (
    <div className="flex min-h-screen flex-col bg-offwhite">
      <PublicNav />
      <main className="flex-1">{children}</main>
      <Footer />
    </div>
  );
}

export function TierBadge({ tier }) {
  const t = TIERS[tier] || TIERS.free;
  return <span className={`inline-flex items-center rounded px-2 py-0.5 text-[11px] font-bold uppercase tracking-wider ${t.color}`}>{t.label}</span>;
}

export function LockBadge({ tier }) {
  return (
    <span className="inline-flex items-center gap-1 rounded bg-navy/90 px-2 py-1 text-[11px] font-semibold text-white">
      <Lock className="h-3 w-3" strokeWidth={2} /> {TIERS[tier]?.label || "Locked"}
    </span>
  );
}

export function SectionHeading({ overline, title, subtitle, light = false }) {
  return (
    <div className="max-w-2xl">
      {overline && <p className="mb-3 flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-gold-dark"><span className="inline-block h-px w-6 bg-gold" />{overline}</p>}
      <h2 className={`font-heading text-3xl font-bold tracking-tight sm:text-4xl ${light ? "text-white" : "text-navy"}`}>{title}</h2>
      {subtitle && <p className={`mt-3 text-base leading-relaxed ${light ? "text-slate-400" : "text-slate-600"}`}>{subtitle}</p>}
    </div>
  );
}

export function Loading({ label = "Loading…" }) {
  return (
    <div className="flex items-center justify-center py-24 text-slate-500" data-testid="loading-state">
      <Loader2 className="mr-2 h-5 w-5 animate-spin" /> {label}
    </div>
  );
}

export function EmptyState({ title = "Nothing here yet", subtitle, action }) {
  return (
    <div className="flex flex-col items-center justify-center rounded-xl border border-dashed border-slate-300 bg-white py-16 text-center" data-testid="empty-state">
      <Inbox className="mb-3 h-8 w-8 text-slate-400" strokeWidth={1.5} />
      <p className="font-heading text-lg font-semibold text-navy">{title}</p>
      {subtitle && <p className="mt-1 max-w-sm text-sm text-slate-500">{subtitle}</p>}
      {action}
    </div>
  );
}

export function ResourceCard({ r }) {
  return (
    <Link to={`/resources/${r.slug}`} data-testid={`resource-card-${r.id}`}
      className="group relative flex flex-col overflow-hidden rounded-xl border border-slate-200 bg-white transition-all duration-200 hover:-translate-y-1 hover:shadow-[0_12px_40px_rgb(0,0,0,0.08)]">
      <div className="relative aspect-[16/10] overflow-hidden bg-slate-100">
        {r.cover_image ? (
          <img src={r.cover_image} alt={r.title} className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105" />
        ) : <div className="h-full w-full bg-gradient-to-br from-navy to-navysurface" />}
        <div className="absolute left-3 top-3 flex gap-2">
          {r.featured && <span className="inline-flex items-center gap-1 rounded bg-gold px-2 py-1 text-[11px] font-bold text-navy"><Star className="h-3 w-3" strokeWidth={2.5} /> Featured</span>}
          {r.locked && <LockBadge tier={r.membership_requirement} />}
        </div>
      </div>
      <div className="flex flex-1 flex-col p-5">
        <div className="mb-2 flex items-center gap-2 text-[11px] font-semibold uppercase tracking-wider text-slate-400">
          <span>{r.category}</span><span>·</span><span>{r.resource_format}</span>
        </div>
        <h3 className="font-heading text-lg font-semibold leading-snug text-navy group-hover:text-emerald">{r.title}</h3>
        <p className="mt-2 line-clamp-2 flex-1 text-sm text-slate-600">{r.short_description}</p>
        <div className="mt-4 flex items-center justify-between">
          <TierBadge tier={r.membership_requirement} />
          <span className="text-xs font-medium text-slate-400">{r.download_count || 0} downloads</span>
        </div>
      </div>
    </Link>
  );
}
